import { Button, Divider, Stack, Typography } from '@mui/material'
import { useWindowHeight } from '@react-hook/window-size'
import { MouseEventHandler, useEffect, useMemo, useRef } from 'react'
import { IconButton, PointDisplay } from 'utoniq-ui'

import {
  Maybe,
  SkuTagFragment,
  SkuTypeFragment,
  StoreItemDetailFragment,
} from '@/clients/utoniq-core/schema'
import { ContentFrame } from '@/components/layouts/ContentFrame'
import { BottomSheet, QuantityPicker } from '@/components/organisms'
import { SkuType } from '@/components/organisms/store/SkuType'
import { useRouter, useTranslation } from '@/core'
import { Cell, Organism, OrganismFrame } from '@/core/architecture'
import { encodeSkuId, getIsSoldOut } from '@/core/sku'

type SkuUnitSelectBottomSheetProps = {
  loading: boolean
  open: boolean
  disabled: boolean
  label: string
  onClose: () => void
  item?: StoreItemDetailFragment
  itemId: string
  purchaseNumber: number
  maximumPurchasableNumber: number
  skuValue?: string
  formattedItemPrice: string
  skuSettings?: Maybe<SkuTypeFragment>
  onSelect: (sku?: SkuTagFragment) => void
  onChangePurchaseNumber: (value: number) => void
  onPurchase: MouseEventHandler<HTMLButtonElement>
  responsive?: boolean
}

export const SkuUnitSelectBottomSheet: Organism<SkuUnitSelectBottomSheetProps> =
  ({
    open,
    loading,
    disabled,
    label,
    onClose,
    itemId,
    item,
    skuValue,
    skuSettings,
    onSelect,
    formattedItemPrice,
    purchaseNumber,
    maximumPurchasableNumber,
    onChangePurchaseNumber,
    onPurchase,
    responsive = false,
  }) => {
    const { t } = useTranslation('storeItemDetail')
    const bottomSheetRef = useRef<HTMLIonModalElement>(null)
    const windowHeight = useWindowHeight()

    useEffect(() => {
      if (!open) {
        bottomSheetRef.current?.dismiss()
      }
    }, [open])

    useEffect(() => {
      const bottomSheet = bottomSheetRef.current
      return () => {
        bottomSheet?.dismiss()
      }
    }, [])

    const isSoldOut = useMemo(() => {
      return getIsSoldOut(item, skuValue)
    }, [item, skuValue])

    return (
      <OrganismFrame>
        <BottomSheet
          ref={bottomSheetRef}
          open={open}
          onClose={onClose}
          breakpoints={[0, 330 / windowHeight]}
          initialBreakpoint={330 / windowHeight}
          responsive={responsive}
        >
          <ContentFrame>
            <Stack direction="row" sx={{ py: 4, px: 3 }} alignItems="center">
              <Typography variant="h6" align="center" width="100%">
                {t('typeSelection', '種類選択')}
              </Typography>
              <IconButton.Close size="small" onClick={onClose} />
            </Stack>
            <Stack sx={{ px: 5 }}>
              <SkuType
                value={skuValue}
                skuSettings={skuSettings}
                onSelect={onSelect}
                paymentType={item?.paymentType}
                issuedNumber={item?.issuedNumber}
                hasAlertText={!skuValue}
              />
            </Stack>
            <Divider />
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ px: 5, minHeight: 44 }}
            >
              <Stack flex={1} spacing={1}>
                {formattedItemPrice !== '0' && (
                  <PointDisplay
                    point={formattedItemPrice ?? '0'}
                    custom={{ fontSize: 'medium', variant: 'h2', spacing: 2 }}
                    paymentType={item?.paymentType ?? 'prepaid_point'}
                  />
                )}
              </Stack>

              <Stack spacing={2} alignItems="flex-end">
                {/* 購入数の選択フィールド、購入制限の数まで表示、なければ10個まで */}
                <QuantityPicker
                  value={purchaseNumber}
                  onMinus={() => onChangePurchaseNumber(--purchaseNumber)}
                  onPlus={() => onChangePurchaseNumber(++purchaseNumber)}
                  disabledPlus={maximumPurchasableNumber <= purchaseNumber}
                />
              </Stack>
            </Stack>
            <Stack direction="row" sx={{ p: 5, alignItems: 'center' }}>
              <ButtonGroupCell
                disabled={disabled}
                loading={loading}
                isSoldOut={isSoldOut}
                label={label}
                onPurchase={onPurchase}
                itemId={itemId}
                skuId={skuValue}
                isResaleAvailable={
                  !!item?.isResaleAvailable || !!item?.willResaleAvailable
                }
              />
            </Stack>
          </ContentFrame>
        </BottomSheet>
      </OrganismFrame>
    )
  }

type ButtonGroupCellProps = {
  isSoldOut: boolean
  isResaleAvailable?: boolean
  onPurchase: MouseEventHandler<HTMLButtonElement>
  disabled: boolean
  loading: boolean
  label: string
  itemId: string
  skuId?: string
  responsive?: boolean
}
const ButtonGroupCell: Cell<ButtonGroupCellProps> = ({
  isSoldOut,
  isResaleAvailable,
  disabled,
  loading,
  onPurchase,
  label,
  itemId,
  skuId,
  responsive = false,
}) => {
  const { t } = useTranslation('storeItemDetail')
  const { router, generatePath, routerPages } = useRouter()
  if (isSoldOut) {
    // TODO レスポンシブ対応確認 横幅変わる
    return (
      <Button
        fullWidth={!responsive}
        sx={responsive ? { width: 244, margin: '0 auto' } : undefined}
        size="large"
        color="primary"
        disabled
      >
        {t('soldOut')}
      </Button>
    )
  }
  if (isResaleAvailable) {
    return (
      <Button
        fullWidth={!responsive}
        sx={responsive ? { width: 244, margin: '0 auto' } : undefined}
        size="large"
        color="primary"
        onClick={() =>
          router.push({
            pathname: generatePath(routerPages.ResaleApplicationDetail, {
              itemId,
            }),
            search: `${skuId ? `?skuId=${encodeSkuId(skuId)}` : ''}`,
          })
        }
      >
        {t('duringResale')}
      </Button>
    )
  }
  return (
    <Button
      fullWidth={!responsive}
      sx={responsive ? { width: 244, margin: '0 auto' } : undefined}
      size="large"
      onClick={onPurchase}
      color="primary"
      disabled={disabled || loading}
      loading={loading}
    >
      {label}
    </Button>
  )
}
